import * as React from 'react';
import { connect } from 'react-redux';
import IconButton from '../common/IconButton';
import NewToolbarItemScroller from '../common/toolbar-item-scroller/NewToolbarItemScroller';
import { Level } from './NewHeaderToolbar';
import CurrentToolIcon from './CurrentToolIcon';
import { Dispatch, Action } from 'redux';
import { CurrentTool } from '../../reducers/ui/ui';
import UIAction from '../../actions/ui/UIAction';
import { DialogType } from '../common/SettingDialogContainer';
import { supportsReflowableRendition, getDefaultRendition } from '../../utils/epub';
import { Publication, PageAction, AppStates } from '../../../../..';
import { getReflowablePageForTab, getActiveFrameId } from '../../helpers/pages';
import { getGlobalFrameStore } from '../../services/FrameStore';
import { ViewerToContent } from '../../types/messages';
import { PagesState, tabFocusSelf, TabType } from '../../reducers/pages';
import { setComposerMode } from '../../actions/AppAction';

interface HeaderToolbarBaseLevelProps {
  readonly isVertical: boolean;
  readonly pushLevel: (level: Level) => void;
  readonly setDialogType: (dialogType: DialogType) => void;
  readonly dispatch: Dispatch<Action>;
  readonly publication?: Publication;
  readonly pages: PagesState;
  readonly developMode: boolean;
}

const openReflowableIfAvailable = (
  publication: Publication,
  pages: PagesState,
  dispatch: Dispatch<Action>,
) => {
  const { tabs, activeTab } = pages;

  // リフロー動的生成を優先
  const tab = tabs[activeTab];
  if (tab.focus === tabFocusSelf && tab.type === TabType.LinearPages) {
    const item = getDefaultRendition(publication).linearItems[tab.currentPage];

    if (item.fxl && item.supportsReflowableContentGeneration) {
      getGlobalFrameStore().postMessage(
        getActiveFrameId(pages),
        ViewerToContent.MessageType.GetReflowableContent,
      );

      return;
    }
  }

  if (supportsReflowableRendition(publication)) {
    const title = tabs[activeTab].title;
    const pageIndex = getReflowablePageForTab(tabs[activeTab], publication);
    if (pageIndex !== null) {
      dispatch(PageAction.openReflowable(title, pageIndex));
    }
  }
};

const HeaderToolbarBaseLevel: React.SFC<HeaderToolbarBaseLevelProps> = React.memo((props) => {
  const { isVertical, pushLevel, dispatch, publication, pages, developMode } = props;

  const onLearningPen = () => {
    if (!developMode) {
      // WIP
      const msg = [
        '暗記ペン機能 (準備中)\n',
        '教科書紙面、コンテンツ、およびリフロー表示において、暗記学習のために文字を隠す機能を提供します。',
      ].join('');
      alert(msg);
      return;
    }

    UIAction.setCurrentTool(CurrentTool.LearningPen, dispatch);
    pushLevel(Level.LearningPen);
  };
  return <>
    <CurrentToolIcon />

    <NewToolbarItemScroller vertical={isVertical} alignEnd={false} scrollButtons={true}>
      <IconButton
        title="マイリンク"
        rouondRect={true}
        imagePath="images/tool-icons/new/mylink/mylink"
        onClick={() => {
          // WIP
          const msg = [
            'マイリンク機能 (準備中)\n',
            '教科書紙面やコンテンツに対して、',
            '指定したファイルやWebサイトURLへのショートカットのリンクを設定することができます。',
          ].join('');
          alert(msg);
        }} />
      <IconButton
        title="画像貼り付け"
        rouondRect={true}
        imagePath="images/tool-icons/new/attachment/attachment"
        onClick= {() => {
          UIAction.setCurrentTool(CurrentTool.ImagePasting, dispatch);
          pushLevel(Level.ImagePasting);
        }} />
      <IconButton
        title="図形描画"
        rouondRect={true}
        imagePath="images/tool-icons/new/shape/shape"
        onClick= {() => {
          UIAction.setCurrentTool(CurrentTool.ObjectDrawing, dispatch);
          pushLevel(Level.ObjectDrawing);
        }} />
      <IconButton
        title="テキストボックス"
        rouondRect={true}
        imagePath="images/tool-icons/new/txtbox/txtbox"
        onClick= {() => {
          UIAction.setCurrentTool(CurrentTool.Textbox, dispatch);
          pushLevel(Level.Textbox);
        }} />
      <IconButton
        title="機能一覧(教科固有）"
        rouondRect={true}
        imagePath="images/tool-icons/new/list_functions/list_functions"
        onClick={() => { pushLevel(Level.ExtraTools); }} />
      <IconButton
        title="選択"
        rouondRect={true}
        imagePath="images/tool-icons/new/select/select"
        onClick={() => { pushLevel(Level.Selection); }} />
      <IconButton
        title="ズーム"
        rouondRect={true}
        imagePath="images/tool-icons/new/zoom/zoom"
        onClick={() => { pushLevel(Level.Zoom); }} />
      <IconButton
        title="ペン"
        rouondRect={true}
        imagePath="images/tool-icons/new/pen/pen"
        onClick={() => {
          UIAction.setCurrentTool(CurrentTool.FreeHandPen, dispatch);
          pushLevel(Level.FreehandPen);
        }} />
      <IconButton
        title="ゴミ箱"
        imagePath="images/tool-icons/new/trashbox/trashbox"
        rouondRect={true}
        onClick={() => {
          UIAction.setCurrentTool(CurrentTool.AllErasing, dispatch);
          pushLevel(Level.AllErasingUndoRedo);
        }} />
      <IconButton
        title="暗記ペン"
        rouondRect={true}
        imagePath="images/tool-icons/new/memorization/icon_memorization"
        onClick={() => onLearningPen()}
      />
      <IconButton
        title="リフロー"
        rouondRect={true}
        imagePath="images/tool-icons/new/reflow/reflow"
        onClick={() => {
          pushLevel(Level.ReflowableContent);

          // ツールバーをリフロー関連設定レベルへ切り替えると同時にリフローコンテンツがあれば開く
          // FIXME: 仮実装であり最終仕様が未決定
          // 現在の形の場合
          // ・リフローコンテンツの有無は判別出来ない
          // ・一度トップレベルへ戻さないと新たにリフローコンテンツを開けない
          // という問題がある
          if (publication !== undefined) {
            openReflowableIfAvailable(publication, pages, dispatch);
          }
        }} />
      <IconButton
        title="SNE"
        rouondRect={true}
        imagePath="images/tool-icons/new/special_support/special_support"
        onClick={() => { pushLevel(Level.SNE); }} />
      <IconButton
        title="バインダ"
        rouondRect={true}
        imagePath="images/tool-icons/new/note_binder/note_binder"
        onClick={() => {
          // WIP
          if (developMode) {
            dispatch(setComposerMode(true));
            return;
          }
          const msg = [
            'バインダ機能 (準備中)\n',
            '教科書のページに紐づいて、自由に書込みしたり、',
            '画像を貼り付けたりできるノートをまとめたバインダを作れます。\n',
            'また、作成したバインダをオフライン環境で他の人にわたすこともできます',
            '(インポート・エクスポート機能)。',
          ].join('');
          alert(msg);
        }} />
      <IconButton
        title="スクリーンショット"
        rouondRect={true}
        imagePath="images/tool-icons/new/screenshot/screenshot"
        onClick={() => {
          // WIP
          const msg = [
            'スクリーンショット機能 (準備中)\n',
            'ビューアのスクリーンショットを取得し、バインダーに貼り付ける機能を提供します。',
            '授業資料作り、学習まとめ作りに活用できます。',
          ].join('');
          alert(msg);
        }}/>
      <IconButton
        title="アプリ間連携"
        rouondRect={true}
        imagePath="images/tool-icons/new/application_cooperation/application_cooperation"
        onClick={() => {
          // WIP
          const msg = [
            'アプリ間連携機能 (準備中)\n',
            'アプリ版専用の機能です。デジタル教科書・教材で指定した外部アプリを起動・連携することができます。',
          ].join('');
          alert(msg);
        }} />
    </NewToolbarItemScroller>
  </>;
});

const mapStateToProps = (appStates: AppStates) => ({
  publication: appStates.publication,
  pages: appStates.pages,
  developMode: appStates.developMode,
});

export default connect(mapStateToProps)(HeaderToolbarBaseLevel);
